// tinker: endingDrag — a mini piano-roll showing a fixed question-and-answer
// phrase whose FINAL note (config.dragIndex) is the only movable one. Drag it
// up and down — it snaps to scale tones inside the piano window — then replay
// to hear how the ending changes the punctuation. The ending's degree number
// is shown as it moves. Always ready to continue.
//
// config: { phrase, dragIndex?, scaleOnly?, prompt? } — phrase in the
// standard model ({ notes: [{ midi, at, dur }] }); dragIndex defaults to the
// phrase's final note (latest `at`, ties to the highest pitch).

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  PIANO_RANGE,
  isInScale,
  degreeOf,
  DEGREE_PERSONA,
  phraseLength,
} from "../../../lib/homeKeyTheory";
import { PATCHES, playPhrase, playNote, resumeAudio } from "../../../lib/homeKeyAudio";

const ROWS = PIANO_RANGE.high - PIANO_RANGE.low + 1;
const ROW_H = 100 / ROWS; // viewBox is 100 units tall
const NOTE_H = ROW_H * 0.8;
const yFor = (midi) => (PIANO_RANGE.high - midi) * ROW_H + ROW_H * 0.1;

// Index of the phrase's final note: latest `at`, ties to the highest pitch
// (mirrors the theory lib's lastNote).
const finalNoteIndex = (phrase) => {
  let idx = -1;
  (phrase.notes || []).forEach((note, i) => {
    if (idx < 0) {
      idx = i;
      return;
    }
    const best = phrase.notes[idx];
    if (note.at > best.at || (note.at === best.at && note.midi > best.midi)) idx = i;
  });
  return idx;
};

const clampToRange = (midi) =>
  Math.max(PIANO_RANGE.low, Math.min(PIANO_RANGE.high, midi));

// Nearest scale tone to `midi` within the piano window (ties prefer down).
const snapToScale = (midi) => {
  const c = clampToRange(midi);
  for (let off = 0; off <= 12; off += 1) {
    if (c - off >= PIANO_RANGE.low && isInScale(c - off)) return c - off;
    if (c + off <= PIANO_RANGE.high && isInScale(c + off)) return c + off;
  }
  return c;
};

const nextScaleTone = (midi, dir) => {
  for (let m = midi + dir; m >= PIANO_RANGE.low && m <= PIANO_RANGE.high; m += dir) {
    if (isInScale(m)) return m;
  }
  return midi;
};

export default function TinkerEndingDrag({ moment, onReadyChange }) {
  const config = moment.config || {};
  const phrase = config.phrase || { notes: [] };
  const dragIdx =
    config.dragIndex != null && phrase.notes[config.dragIndex]
      ? config.dragIndex
      : finalNoteIndex(phrase);

  const [endMidi, setEndMidi] = useState(() =>
    dragIdx >= 0 ? snapToScale(phrase.notes[dragIdx].midi) : PIANO_RANGE.low
  );
  const [playing, setPlaying] = useState(false);
  const svgRef = useRef(null);
  const dragRef = useRef(null); // { y, midi } at drag start
  const playRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (onReadyChange) onReadyChange(true);
  }, [onReadyChange]);

  const stopAll = useCallback(() => {
    if (playRef.current && playRef.current.stop) playRef.current.stop();
    playRef.current = null;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    setPlaying(false);
  }, []);
  useEffect(() => () => stopAll(), [stopAll]);

  const phraseNow = () => ({
    notes: phrase.notes.map((note, i) =>
      i === dragIdx ? { ...note, midi: endMidi } : note
    ),
  });

  const replay = () => {
    resumeAudio();
    stopAll();
    setPlaying(true);
    const current = phraseNow();
    const handle = playPhrase(current, PATCHES.softKeys);
    playRef.current = handle && handle.stop ? handle : null;
    const durMs = (handle && handle.durMs) || phraseLength(current) || 2000;
    timerRef.current = setTimeout(() => setPlaying(false), durMs + 300);
  };

  const moveTo = (midi) => {
    const next = snapToScale(midi);
    if (next !== endMidi) {
      setEndMidi(next);
      resumeAudio();
      playNote(PATCHES.softKeys, next, 300);
    }
  };

  const onDragStart = (e) => {
    e.preventDefault();
    const el = e.currentTarget;
    if (el.setPointerCapture && e.pointerId != null) {
      try {
        el.setPointerCapture(e.pointerId);
      } catch (err) {
        // stale pointer id — harmless
      }
    }
    dragRef.current = { y: e.clientY, midi: endMidi };
  };
  const onDragMove = (e) => {
    const d = dragRef.current;
    if (!d || !svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const pxPerRow = rect.height / ROWS;
    if (!pxPerRow) return;
    const delta = Math.round((d.y - e.clientY) / pxPerRow);
    moveTo(d.midi + delta);
  };
  const onDragEnd = () => {
    dragRef.current = null;
  };

  const totalMs = Math.max(phraseLength(phrase) || 0, 1);
  const sx = 100 / totalMs;
  const endDegree = degreeOf(endMidi);

  return (
    <div className="hk-moment-body">
      <p>
        {config.prompt ||
          "The question is fixed. Drag the answer's last note up or down and replay — hear how the ending changes the punctuation."}
      </p>

      <div className="hk-roll">
        <svg
          ref={svgRef}
          viewBox="0 0 100 100"
          width="100%"
          height="220"
          preserveAspectRatio="none"
          aria-label="mini piano roll: a question and answer phrase with a draggable last note"
        >
          {/* faint lanes for the scale tones (home lanes marked) */}
          {Array.from({ length: ROWS }, (_, i) => {
            const midi = PIANO_RANGE.high - i;
            if (!isInScale(midi)) return null;
            const home = midi % 12 === 7;
            return (
              <rect
                key={midi}
                className={`hk-roll-lane${home ? " hk-roll-lane--home" : ""}`}
                x="0"
                y={(PIANO_RANGE.high - midi) * ROW_H}
                width="100"
                height={ROW_H}
                fill={home ? "rgba(232,168,124,0.12)" : "rgba(157,148,196,0.07)"}
              />
            );
          })}
          {phrase.notes.map((note, i) => {
            const drag = i === dragIdx;
            const midi = drag ? endMidi : note.midi;
            return (
              <rect
                key={i}
                className={`hk-roll-note${drag ? " hk-roll-note--drag" : ""}`}
                x={note.at * sx}
                y={yFor(midi)}
                width={Math.max(note.dur * sx, 1)}
                height={NOTE_H}
                rx="0.8"
                style={drag ? { cursor: "ns-resize", touchAction: "none" } : undefined}
                tabIndex={drag ? 0 : undefined}
                role={drag ? "slider" : undefined}
                aria-label={drag ? "the answer's last note" : undefined}
                aria-valuemin={drag ? PIANO_RANGE.low : undefined}
                aria-valuemax={drag ? PIANO_RANGE.high : undefined}
                aria-valuenow={drag ? endMidi : undefined}
                aria-valuetext={drag && endDegree ? `degree ${endDegree}` : undefined}
                onPointerDown={drag ? onDragStart : undefined}
                onPointerMove={drag ? onDragMove : undefined}
                onPointerUp={drag ? onDragEnd : undefined}
                onPointerCancel={drag ? onDragEnd : undefined}
                onKeyDown={
                  drag
                    ? (e) => {
                        if (e.key === "ArrowUp") {
                          e.preventDefault();
                          moveTo(nextScaleTone(endMidi, 1));
                        } else if (e.key === "ArrowDown") {
                          e.preventDefault();
                          moveTo(nextScaleTone(endMidi, -1));
                        }
                      }
                    : undefined
                }
              />
            );
          })}
        </svg>
      </div>

      <p className="hk-roll-caption" aria-live="polite">
        {endDegree
          ? `ending on ${endDegree} — ${DEGREE_PERSONA[endDegree]}`
          : "drag the last note"}
      </p>

      <button
        type="button"
        className={`hk-demo-btn${playing ? " hk-demo-btn--on" : ""}`}
        onClick={replay}
      >
        ▶ replay it
      </button>
    </div>
  );
}
